<template>
  <CheckoutStep>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items
        :methods-features="['bouygues_in_store']"
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-payment-availability
      >
        <template #title>
          {{ i18n(translations.cartSummaryTitle) }}
        </template>
      </CheckoutSummary>
    </template>

    <div aria-live="polite" role="status">
      <EmptyCart :items />

      <CartContent
        :is-loading="loading"
        :items
        :summary
        @next="handleNextStep"
        @update="emit('update', $event)"
      >
        <Swap
          :items
          :swap-offer
          :total-price-with-swap="summary.totalPriceAfterBuyback"
          zone="bouygues-in-store-cart"
          @updated="emit('update')"
        />
      </CartContent>

      <RevInfoBlock
        v-if="FF_BOUYGUES_IN_STORE_RETURN_INFO_BLOCK"
        class="mt-8"
        :content="i18n(translations.returnInfoBlockDescription)"
        :icon="IconGift"
        :title="i18n(translations.returnInfoBlockTitle)"
        variant="info"
      />
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo, useRuntimeConfig } from '#imports'
import { onMounted, provide } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '@backmarket/nuxt-layer-checkout/components/CheckoutSummary/CheckoutSummary.vue'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconGift } from '@ds/icons/IconGift'

import CartContent from '~/scopes/checkout/components/Cart/components/CartContent/CartContent.vue'
import EmptyCart from '~/scopes/checkout/components/Cart/components/EmptyCart/EmptyCart.vue'
import Swap from '~/scopes/checkout/components/Cart/components/Swap/Swap.vue'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'

import translations from './CartStep.translations'

type CartItem = ApiSpecs['/bm/cart']['GET']['response']['cartItems'][number]
type CartSummary = ApiSpecs['/bm/cart']['GET']['response']['summary']

const props = defineProps<{
  items: CartItem[]
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  summary: CartSummary
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  loading: boolean
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', updatedItem?: CartItem[]): void
}>()

const i18n = useI18n()
const { trackFunnel } = useCheckoutTracking()
const { FF_BOUYGUES_IN_STORE_RETURN_INFO_BLOCK } = useRuntimeConfig().public

// We don't want user to be able to modify the cart from bouygues in-store experience
provide<boolean>('isCartLocked', true)

function handleNextStep() {
  navigateTo({
    name: CHECKOUT.IN_STORE_SALES.BOUYGUES_TELECOM.ADDRESSES,
  })
}

onMounted(async () => {
  trackFunnel(props.items, props.swapOffer)
})
</script>
